import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton, Paper, Tooltip, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useIntl } from "react-intl";
import { formatDurationHours, formatTimestamp, toValidDate } from "../../cmms/cmmsFormat";
import { CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import { getDataGridPanelSx } from "../../cmms/assetHierarchyStyles";
import { applyColumnWidths, readStoredJson, reorderColumns } from "../../cmms/cmmsGridPrefs";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { tokens } from "../../../assets/theme";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import { getPriorityBadge, getStatusBadge, getTypeBadge } from "../workOrderEnums";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";

const columnOrderLocalStorageName = "cmmsWorkOrders-columnOrder";
const columnWidthsLocalStorageName = "cmmsWorkOrders-columnWidths";
// The third of the same kind: order and width have been remembered here from the start,
// and which columns you want is the same sort of preference as how wide you want them.
const columnVisibilityLocalStorageName = "cmmsWorkOrders-columnVisibility";

/*
 * The columns hidden unless someone asks for them.
 *
 * `id` has been hidden since the Columns panel arrived. The planned dates join it because this
 * grid is a work list first — you turn them on when you are planning, and the details panel and
 * the form show them either way. The estimate is the same kind of column, for the same reason.
 */
const defaultColumnVisibility = {
  id: false,
  plannedStartDate: false,
  plannedEndDate: false,
  estimatedHours: false,
};

const initColumnsFromLocalStorage = (cols) => {
  let out = cols;
  const savedOrder = readStoredJson(columnOrderLocalStorageName, null);
  if (savedOrder) out = reorderColumns(savedOrder, out);
  const savedWidths = readStoredJson(columnWidthsLocalStorageName, null);
  if (savedWidths) out = applyColumnWidths(savedWidths, out);
  return out;
};

// Exported for the asset panel's history tab, so a status reads the same in both grids.
export const renderBadge = (badge) => (
  <Chip
    label={badge.label}
    size="small"
    sx={{ backgroundColor: badge.bg, color: badge.text, fontWeight: 700 }}
  />
);

/*
 * A timestamp column that sorts and filters by the instant, not by the text it shows.
 *
 * `createdAt` used to hand the grid its formatted text, so it sorted as text: right in Swedish
 * (YYYY-MM-DD), wrong across month and year ends in English (MM/DD/YYYY). The planned dates
 * handed it the raw ISO string, which sorted right only while every value was spelled alike.
 * A Date settles both, and gives the toolbar's Filters panel "is after" and "is before" in place
 * of "contains". The cell, the CSV export and the print read valueFormatter, so what is shown
 * does not change.
 */
const dateColumn = (intl, field, message) => ({
  field,
  headerName: intl.formatMessage(message),
  type: "dateTime",
  width: 170,
  valueGetter: (params) => toValidDate(params.row[field]),
  valueFormatter: (params) => formatTimestamp(intl, params.value),
});

/*
 * MUI's GridToolbar, part for part: Columns, Filters, Density and Export, the same toolbar
 * features/home, oeegrid, material and productionPlan mount. Its labels come from the MUI locale
 * passed to the grid, so this adds no strings of our own.
 *
 * Rebuilt rather than mounted so the caller can put something in the right-hand corner, where
 * GridToolbar keeps its quick filter. The list page puts its date range there. ml: auto keeps it on
 * the right when the row wraps on a narrow screen, and py gives an outlined field's floating label
 * room under the toolbar's 4px top padding.
 *
 * At module level, with `end` handed in through slotProps. A toolbar component made during render
 * would be a new component on every render, so the grid would remount it on every keystroke and
 * a picker in it would lose its focus and its open calendar.
 */
function WorkOrdersToolbar({ end }) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      {end && <Box sx={{ ml: "auto", py: 1 }}>{end}</Box>}
    </GridToolbarContainer>
  );
}

export default function WorkOrdersDataGrid({
  workOrders,
  isLoading,
  assetNodeNameById,
  userNameById,
  onRowClick,
  onEdit,
  onDelete,
  emptyState,
  // The toolbar's right-hand corner. The machine page's tab passes nothing and keeps the plain toolbar.
  toolbarEnd,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const apiRef = useGridApiRef();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;

  // Lazy initialiser so the read happens once rather than on every render, and guarded like the
  // two sibling keys: a value that will not parse costs a preference, not the page.
  // The saved model wins where it has an opinion, and defaultColumnVisibility fills the rest.
  // Plain `readStoredJson(key, defaultColumnVisibility)` would show the two planned dates to
  // anyone who had already touched the panel once, because their saved model predates them.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => ({
    ...defaultColumnVisibility,
    ...readStoredJson(columnVisibilityLocalStorageName, null),
  }));

  const baseColumns = useMemo(
    () => [
      { field: "id", headerName: intl.formatMessage(messages.columnId), width: 80 },
      { field: "title", headerName: intl.formatMessage(messages.fieldTitle), flex: 2, minWidth: 180 },
      {
        field: "assetNodeName",
        headerName: intl.formatMessage(messages.fieldAsset),
        flex: 1,
        minWidth: 140,
        valueGetter: (params) =>
          assetNodeNameById?.get(params.row.assetNodeId) ?? `#${params.row.assetNodeId}`,
      },
      {
        field: "workOrderType",
        headerName: intl.formatMessage(messages.fieldType),
        width: 130,
        renderCell: (params) => renderBadge(getTypeBadge(params.row.workOrderType, intl)),
      },
      {
        field: "priority",
        headerName: intl.formatMessage(messages.fieldPriority),
        width: 110,
        renderCell: (params) => renderBadge(getPriorityBadge(params.row.priority, intl)),
      },
      {
        field: "status",
        headerName: intl.formatMessage(messages.fieldStatus),
        width: 130,
        renderCell: (params) => renderBadge(getStatusBadge(params.row.status, intl)),
      },
      // The crew before the person, the order the form picks them in. The name comes on the row,
      // so unlike the asset and the assignee it needs no map to resolve.
      {
        field: "tradeName",
        headerName: intl.formatMessage(messages.fieldTrade),
        flex: 1,
        minWidth: 130,
        valueGetter: (params) => params.row.tradeName ?? "",
      },
      {
        field: "assignedResourceName",
        headerName: intl.formatMessage(messages.fieldAssigned),
        flex: 1,
        minWidth: 140,
        valueGetter: (params) => {
          const id = params.row.assignedResourceId;
          if (!id) return "";
          return userNameById?.get(id) ?? `#${id}`;
        },
      },
      // Hidden by default; see defaultColumnVisibility above.
      dateColumn(intl, "plannedStartDate", messages.fieldPlannedStart),
      dateColumn(intl, "plannedEndDate", messages.fieldPlannedEnd),
      // A number column, so it sorts by the hours rather than by "10h 00m" before "2h 00m", and
      // formatted the way the details panel shows it.
      {
        field: "estimatedHours",
        headerName: intl.formatMessage(messages.fieldEstimatedHours),
        type: "number",
        width: 140,
        valueFormatter: (params) => formatDurationHours(intl, params.value),
      },
      dateColumn(intl, "createdAt", messages.fieldCreated),
      // No handler, no column. The buttons were previously rendered unconditionally with
      // `onEdit?.()` / `onDelete?.()`, so a caller that supplies neither — the machine page's
      // maintenance tab is the first — showed managers two buttons that silently did nothing.
      ...(onEdit || onDelete ? [{
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        // Kept out of the Columns panel. With no headerName it would list as a blank
        // unlabelled switch, and switching it off would quietly remove Edit and Delete.
        hideable: false,
        renderCell: (params) => (
          <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
            <Box onClick={(event) => event.stopPropagation()}>
              <Tooltip title={intl.formatMessage(cmmsMessages.edit)}>
                <IconButton
                  size="small"
                  aria-label={intl.formatMessage(messages.gridEditAria)}
                  onClick={() => onEdit?.(params.row)}
                  sx={{ backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title={intl.formatMessage(cmmsMessages.delete)}>
                <IconButton
                  size="small"
                  aria-label={intl.formatMessage(messages.gridDeleteAria)}
                  onClick={() => onDelete?.(params.row)}
                  sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </HideIfNoRole>
        ),
      }] : []),
    ],
    [assetNodeNameById, userNameById, onEdit, onDelete, colors, intl],
  );

  const columns = useMemo(() => initColumnsFromLocalStorage(baseColumns), [baseColumns]);

  // The grid stays mounted while loading so the header row and height hold their place;
  // the skeleton fills the body instead of the default indeterminate bar, which leaves
  // the panel blank and makes a slow request look like a failed one.
  const gridSlots = useMemo(
    () => ({
      toolbar: WorkOrdersToolbar,
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={8} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — the machine tab does not, and an overlay rendering
      // `undefined` would cover the grid with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  const handleColumnOrderChange = () => {
    if (!apiRef.current) return;
    // Every column, not just the visible ones: a hidden column left out here is a hidden column
    // missing from the saved order, and it comes back at the far right once it is unhidden.
    const fields = apiRef.current.getAllColumns().map((c) => c.field);
    localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(fields));
  };

  const handleColumnResize = (params) => {
    const { colDef, width } = params;
    const map = readStoredJson(columnWidthsLocalStorageName, {});
    map[colDef.field] = width;
    localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(map));
  };

  const handleColumnVisibilityModelChange = (model) => {
    setColumnVisibilityModel(model);
    localStorage.setItem(columnVisibilityLocalStorageName, JSON.stringify(model));
  };

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
          // The toolbar's buttons default to the theme's primary, which here is the surface
          // colour — dark lettering on a dark bar. gray[100] is the foreground token and inverts
          // with the mode, so this reads in both. The same three lines the other 28 grids in the
          // app carry; GridToolbar was mounted here without them.
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.gray[100]} !important`,
          },
          "& .MuiDataGrid-row": { cursor: "pointer" },
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          loading={isLoading}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={workOrders || []}
          getRowId={(row) => row.id}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnResize={handleColumnResize}
          onRowClick={(params) => onRowClick?.(params.row)}
          slots={gridSlots}
          slotProps={{ panel: { sx: getDataGridPanelSx(theme.palette.mode) }, toolbar: { end: toolbarEnd } }}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
